import React, { FunctionComponent, useMemo } from 'react';
import { User } from '/src/domain/shared/User';
import {
  Protocol,
  Challenge,
  IppodromItem,
  instanceOfSingleRaceRecord,
  instanceOfTeamRecord,
  SingleRaceRecord,
} from '/src/domain/challenges/ChallengeProtocolData';
import { Box, Grid, SxProps } from '@mui/material';
import { ClubRecord, ClubRecordProps } from './ClubRecord';
import { useTranslation } from 'react-i18next';
import { isNumber as _isNumber } from 'lodash';

interface ClubsProtocolProps {
  protocols: Protocol[];
  ippodrom: IppodromItem[];
  currentUser?: User;
  challenge: Challenge;
  protocolsContainerRef: React.RefObject<HTMLDivElement>;
}

interface ClubsProtocol {
  [key: string]: Omit<ClubRecordProps, 'place' | 'protocolsContainerRef'>;
}

export const ClubsProtocolView: FunctionComponent<ClubsProtocolProps> = ({
  protocols,
  ippodrom,
  currentUser,
  challenge,
  protocolsContainerRef,
}) => {
  const { t } = useTranslation();

  const clubsProtocol: ClubsProtocol = useMemo(() => {
    const clubsProtocol: ClubsProtocol = {};

    const addClub = (r: SingleRaceRecord | IppodromItem, place?: number) => {
      const clubId = r.participant.clubId;
      const clubName = r.participant.clubName;
      if (!_isNumber(r.place) || !clubId || !clubName) return;

      if (!clubsProtocol[clubId]) {
        clubsProtocol[clubId] = {
          id: clubId,
          clubKey: r.participant.clubKey,
          name: clubName,
          logoUrl: r.participant.clubLogoUrl,
          records: [],
          averagePlace: 0,
        };
      }

      clubsProtocol[clubId].records.push({
        ...r,
        place: _isNumber(place) ? place : r.place,
      });
    };

    if (ippodrom.length) {
      for (const record of ippodrom) {
        addClub(record, record.place + 1);
      }
    } else {
      for (const protocol of protocols) {
        for (const record of protocol.records) {
          if (instanceOfSingleRaceRecord(record)) {
            addClub(record);
          }
          if (instanceOfTeamRecord(record)) {
            record.records.forEach((r) => addClub(r, record.place));
          }
        }
      }
    }

    Object.keys(clubsProtocol).forEach(clubId => {
      if (clubsProtocol[clubId].records.length < 5) {
        delete clubsProtocol[clubId];
      }
    });

    Object.values(clubsProtocol).forEach((club) => {
      club.records.sort((a, b) => a.place! - b.place!);
      const topPlaces = club.records.slice(0, 5).map((record) => record.place!);
      club.averagePlace =
        topPlaces.reduce((sum, place) => sum + place, 0) / topPlaces.length;
    });

    return clubsProtocol;
  }, [protocols, ippodrom]);

  if (!Object.keys(clubsProtocol).length) return null;

  return (
    <Box
      sx={{
        color: 'primary.main',
        fontWeight: 500,
        fontSize: '0.9rem',
      }}
    >
      <Grid
        container
        justifyContent="center"
        alignItems="center"
        sx={{
          padding: '0.5rem',
        }}
      >
        <Grid item xs={2} container style={{ paddingLeft: 0 }} sx={sxCell}>
          #
        </Grid>
        <Grid item xs={4} container sx={sxCell}>
          {t('club')}
        </Grid>
        <Grid item xs={3} container></Grid>
        <Grid
          item
          xs={2}
          container
          sx={{
            ...sxCell,
            justifyContent: 'flex-end',
          }}
        >
          {t('averagePlace')}
        </Grid>
      </Grid>
      {Object.values(clubsProtocol)
        .sort((a, b) => a.averagePlace - b.averagePlace)
        .map((c, index) => (
          <ClubRecord key={c.id} {...c} place={index + 1} protocolsContainerRef={protocolsContainerRef}/>
        ))}
    </Box>
  );
};

const sxCell: SxProps = {
  display: 'flex',
  alignItems: 'center',
};
